import React, { useEffect, useState, useRef, useCallback } from "react";
import { addNotification } from "store/slices/uiSlice.js";
import styled from "styled-components";
import { getTheme } from "Theme";
import {
  faPlusCircle,
  faBars,
  faUser,
  faFile,
  faUserFriends,
  faClock,
} from "@fortawesome/free-solid-svg-icons";
import BugReport from "./BugReport.jsx";
import axios from "axios";
import cookie from "js-cookie";
import { clearCookies } from "globalResources/helpers";
import { useDispatch, useSelector } from "react-redux";
import { clearUser, setAccounts, setUser } from "store/slices/userSlice";
import { errorHandler } from "globalResources/util";
import { Link } from "react-router-dom";
import { useLocation } from "react-router-dom";
import { useHistory } from "react-router";
import { Clickable, IconButton } from "./index.js";
import NavigationDrawer from "./NavigationDrawer.jsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import NotificationsDropdown from "./NotificationsDropdowns.jsx";
// import { create } from "yup/lib/Reference.js";

const theme = getTheme();

const Main = styled.div`
  display: flex;
  flex-direction: column;
  position: sticky;
  background: ${theme.PAGE_BACKGROUND};
  top: 0;
  z-index: 90;
`;

const TopBar = styled.div`
  display: flex;
  flex-direction: row;
  background-color: ${theme.MAIN_COLOR};
  height: 45px;
  color: ${theme.TEXT};
  align-items: center;
  @media (max-width: 1100px) {
    padding: 0 5px 0 0;
  }
`;

const BottomBar = styled.div`
  display: flex;
  flex-direction: row;
  height: 28px;
  align-items: center;
  margin: 0 4vw;
  gap: 8px;
  @media (max-width: 1100px) {
    display: none;
  }
`;

const Logo = styled.img`
  width: 50px;
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  cursor: pointer;
  @media (max-width: 1100px) {
    display: none;
  }
`;

// const Profile = styled.img`
//   height: 28px;
//   width: 28px;
//   border: none;
//   margin: 7.5px;
//   border-radius: 100%;
//   background-color: ${theme.ICONS};
// `;

const Route = styled(Link)`
  color: ${(props) => (props.current ? theme.MAIN_COLOR : theme.TEXT)};
  text-decoration: none;
  font-size: 16px;
  padding: 0 5px;
  font-weight: 520;

  :hover {
    color: ${theme.MAIN_COLOR};
  }
`;

const CreateNewBugModal = styled.div`
  color: ${(props) => (props.current ? theme.MAIN_COLOR : theme.TEXT)};
  text-decoration: none;
  font-size: 16px;
  padding: 0 5px;
  font-weight: 520;

  :hover {
    color: ${theme.MAIN_COLOR};
  }
`;
// const SMS = styled.div`
//   font-size: 12px;
//   font-family:
//     -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu,
//     Cantarell, "Open Sans", "Helvetica Neue", sans-serif;
//   font-weight: 600;
//   margin: auto auto auto 4px;
//   cursor: pointer;
//   color: ${theme.MANILLA};
//   padding: 4px;
//   margin: auto 0 0 auto;
// `;

const ProfileSelectButton = styled.div`
  display: flex;
  flex-direction: row;
  border-radius: 5px;
  padding: 5px;
  cursor: pointer;
  color: white;
  :hover {
    background: ${theme.SECONDARY_COLOR};
    color: ${theme.MAIN_COLOR};
  }
`;

const Name = styled.div`
  font-size: 18px;
  font-family:
    -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu,
    Cantarell, "Open Sans", "Helvetica Neue", sans-serif;
  font-weight: 700;
  margin: auto auto auto 4px;
  cursor: pointer;
  padding: 4px;
`;

const CreateDropdownMenu = styled.div`
  position: absolute;
  top: 40px;
  right: 10px;
  background: ${theme.PAGE_BACKGROUND};
  box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.1);
  border-radius: 6px;
  z-index: 100;
  width: 150px;
`;

const CreateItem = styled.div`
  padding: 10px 15px;
  font-weight: 600;
  cursor: pointer;
  display: grid;
  grid-template-columns: 1fr 4fr;
  align-items: center;
`;

const routes = [
  {
    name: "Dashboard",
    link: "/dashboard",
  },
  {
    name: "Users",
    link: "/users",
    permission: "manageUsers",
    clientPermission: "users",
  },
  {
    name: "Groups",
    link: "/groups",
    permission: "manageGroups",
  },
  {
    name: "Time Entries",
    link: "/timesheets",
    permission: "manageUserTimeSheets",
    clientPermission: "timeentries",
  },
  {
    name: "Clients",
    link: "/clients",
    permission: "manageClients",
  },
  {
    name: "Accounting",
    link: "/accounting",
    // billing and adjustments need manageReports, payroll needs managePayroll
    anyPermission: ["manageReports", "managePayroll"],
    clientPermission: "reports",
  },
  {
    name: "Analytics",
    link: "/analytics",
    permission: "viewAnalytics",
    clientPermission: "analytics",
  },
  {
    name: "Company",
    link: "/company",
    // either one opens the page (settings are read-only without manageCompany)
    anyPermission: ["manageCompany", "manageJoinRequests"],
  },
  {
    name: "Bugs",
    link: "/bugs",
  },
];

const NavigationBar = () => {
  const [bug, setBug] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const dispatch = useDispatch();
  const user = useSelector((state) => state.current.user);
  const accounts = useSelector((state) => state.current.accounts);
  const account = useSelector((state) => state.current.account);
  const permissions = useSelector((state) => state.current.permissions);
  const location = useLocation();
  const history = useHistory();

  const ref = useRef(null);
  const drawerRef = useRef(null);

  const [createDropdownOpen, setCreateDropdownOpen] = useState(false);

  const getAccounts = useCallback(() => {
    axios.get("/root/users").then((res) => {
      dispatch(setAccounts(res.data.users));
    });
  }, [dispatch]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (ref.current && !ref.current.contains(event.target)) {
        setCreateDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [ref]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (drawerRef.current && !drawerRef.current.contains(event.target)) {
        setDrawerOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [drawerRef]);

  const checkPermission = (route) => {
    if (route.anyPermission && user.accountType !== "client")
      return (
        user.accountType === "super" ||
        route.anyPermission.some((p) => permissions.includes(p))
      );
    if (route.anyPermission && user.accountType === "client") {
      if (!route.clientPermission) return false;
      if (route.clientPermission === "all") return true;
      return permissions.includes(route.clientPermission);
    }
    if (!route.permission || user.accountType === "super") {
      return true;
    } else if (user.accountType === "client") {
      if (!route.clientPermission) return false;
      else if (route.clientPermission === "all") return true;
      return permissions.includes(route.clientPermission);
    } else return permissions.includes(route.permission);
  };

  const switchAccounts = (userId, accountId) => {
    axios.patch("/root/switch", { userId, accountId }).then(async (res) => {
      dispatch(clearUser());
      await clearCookies();
      //set token
      cookie.set("token", res.data.token, { expires: 0.9 }); //set cookie to expire in one day
      cookie.set("type", "user", { expires: 0.9 });
      cookie.set(
        "name",
        res.data.user.firstName + " " + res.data.user.lastName,
      );
      cookie.set("id", res.data.user._id);
      axios.defaults.headers.common["Authorization"] = res.data.token;
      window.location.reload();
    });
  };

  const fetchCurrent = useCallback(async () => {
    try {
      let res = await axios.get(`/user/current`);
      dispatch(setUser({ ...res.data }));
      getAccounts();
      if (res.data.needsPassword) {
        history.push("/user/password");
        return;
      }
    } catch (err) {
      const { message } = errorHandler(err);
      dispatch(addNotification({ type: "error:", message }));
    }
  }, [dispatch, getAccounts, history]);

  useEffect(() => {
    fetchCurrent();
  }, [fetchCurrent]);

  return (
    <Main>
      <TopBar>
        <ProfileSelectButton onClick={() => setDrawerOpen(true)}>
          <IconButton icon={faBars} />
          <Name>{account ? account.name : " Hotel Solutions "}</Name>
        </ProfileSelectButton>
        <Logo src="/tcs.svg" onClick={() => history.push("/")} />
        <NotificationsDropdown />
        <Clickable onClick={() => setCreateDropdownOpen(!createDropdownOpen)}>
          <IconButton icon={faPlusCircle} />
        </Clickable>
        {createDropdownOpen && (
          <CreateDropdownMenu ref={ref}>
            <CreateItem
              onClick={() => {
                setCreateDropdownOpen(false);
                history.push("/users?create=true");
              }}
            >
              <FontAwesomeIcon icon={faUser} /> User
            </CreateItem>
            <CreateItem
              onClick={() => {
                setCreateDropdownOpen(false);
                history.push("/groups?create=true");
              }}
            >
              <FontAwesomeIcon icon={faUserFriends} /> Group
            </CreateItem>{" "}
            <CreateItem
              onClick={() => {
                setCreateDropdownOpen(false);
                history.push("/timesheets?create=true");
              }}
            >
              <FontAwesomeIcon icon={faClock} /> Time Entry
            </CreateItem>{" "}
            <CreateItem
              onClick={() => {
                setCreateDropdownOpen(false);
                history.push("/clients?create=true");
              }}
            >
              <FontAwesomeIcon icon={faClock} /> Client
            </CreateItem>
            <CreateItem
              onClick={() => {
                setCreateDropdownOpen(false);
                history.push("/accounting/billing?create=true");
              }}
            >
              <FontAwesomeIcon icon={faFile} /> Invoice
            </CreateItem>
          </CreateDropdownMenu>
        )}
      </TopBar>
      <BottomBar>
        {routes.map(
          (route, index) =>
            checkPermission(route) && (
              <Route
                to={route.link}
                current={
                  location.pathname.includes(route.link) === true
                    ? "true"
                    : undefined
                }
                key={route.name}
              >
                {route.name}
              </Route>
            ),
        )}
        <Clickable style={{ marginLeft: "auto" }}>
          <Route to="/faq" style={{ color: theme.MAIN_COLOR }}>
            Tutorial Page
          </Route>
        </Clickable>
        <Clickable onClick={() => setBug(true)}>
          <CreateNewBugModal style={{ color: theme.NEGATIVE }}>
            Report A Bug
          </CreateNewBugModal>
        </Clickable>
      </BottomBar>
      <BugReport open={bug} onClose={() => setBug(false)} />
      <NavigationDrawer
        drawerOpen={drawerOpen}
        setDrawerOpen={setDrawerOpen}
        drawerRef={drawerRef}
        setBug={setBug}
        userName={user.firstName + " " + user.lastName}
        accountName={account ? account.name : "Hotel Solutions"}
        routes={routes.filter((r) => checkPermission(r))}
        accounts={accounts}
        switchAccounts={switchAccounts}
      />
    </Main>
  );
};

export default NavigationBar;
